import React, { useContext, useReducer, useState, useMemo } from 'react';
import { Segment, Header, Button, Icon } from 'semantic-ui-react';
import Resumo from './Resumo';
import GerarForm from './GerarForm';
import RelatoriosForm from './RelatoriosForm';
import RelatoriosExibicao from './RelatoriosExibicao';
import GerarExibicao from './GerarExibicao';
import { GlobalContext } from 'globalContext';
import request from 'services/http';
import { forkJoin } from 'rxjs';
import { map, tap, finalize } from 'rxjs/operators';
import PageLoader from 'components/PageLoader';
import { useSubscription } from 'utils';
import PageHeader from 'components/PageHeader';
import Table from 'components/Table';
import XLSX from 'xlsx';

function reducer(state, action) {
    switch (action.type) {
        case 'setForm':
            return { ...state, ...action.payload };
        default:
            return state;
    }
}

function PerdasDevedoresDuvidososPage() {
    const [globalState] = useContext(GlobalContext);
    const [registros, setRegistros] = useState([]);
    const [grafico, setGrafico] = useState(null);
    const [acao, setAcao] = useState('resumo');
    const [isFetching, setIsFetching] = useState(true);
    const [fetchData, setFetchData] = useState({
        operacoes: [],
        tipos: [],
        periodos: [],
        cortes: [],
    });
    const [form, dispatch] = useReducer(reducer, {
        empresa: '',
        periodo: '',
        tipo: 90,
        operacao: 1,
        desconsiderarGrupo: true,
        considerarCorte: true,
        dataIni: '',
        dataFim: '',
    });
    const [formRelatorios, dispatchRelatorio] = useReducer(reducer, {
        empresa: 999,
        periodo: '',
        tipo: 90,
        operacao: 1,
    });

    const dados$ = useMemo(
        () =>
            forkJoin({
                periodos: request({
                    url: '/common/dre/periodos',
                    method: 'GET',
                }).pipe(map(({ response }) => (response.periodos ? response.periodos : []))),
                tipos: request({ url: '/dre/pdd/tipos', method: 'GET' }).pipe(
                    map(({ response }) => (response.tipos ? response.tipos : [])),
                ),
                operacoes: request({
                    url: '/dre/pdd/operacoes',
                    method: 'GET',
                }).pipe(map(({ response }) => (response.operacoes ? response.operacoes : []))),
                cortes: request({
                    url: '/dre/pdd/datas-cortes',
                    method: 'GET',
                }).pipe(map(({ response }) => (response.cortes ? response.cortes : []))),
                grafico: request({
                    url: '/dre/pdd/graph',
                    method: 'POST',
                    body: { operacao: 1, tipo: 999 },
                }).pipe(map(({ response }) => (response.dados ? response.dados : []))),
            }).pipe(
                tap(({ periodos, tipos, operacoes, cortes, grafico }) => {
                    setFetchData((prev) => ({
                        ...prev,
                        periodos,
                        tipos,
                        operacoes,
                        cortes,
                    }));
                    setGrafico(grafico);
                }),
                finalize(() => setIsFetching(false)),
            ),
        [setFetchData, setGrafico],
    );

    const { tipos, periodos, operacoes, cortes } = fetchData;
    const gerarFormProps = {
        form,
        dispatch,
        tipos,
        periodos,
        operacoes,
        cortes,
        setRegistros,
    };
    const gerarExibicaoProps = {
        registros,
        form,
        operacoes,
        tipos,
        periodos,
        empresas: globalState.empresas,
        setRegistros,
    };
    const relatoriosFormProps = {
        form: formRelatorios,
        dispatch: dispatchRelatorio,
        tipos,
        periodos,
        operacoes,
        setRegistros,
    };
    const relatoriosExibicaoProps = {
        registros,
        form: formRelatorios,
        operacoes,
        tipos,
        periodos,
        empresas: globalState.empresas,
        setRegistros,
    };
    const resumoProps = { operacoes, tipos, periodos, grafico, setGrafico };

    useSubscription(dados$, isFetching && globalState.empresas !== null);

    const config = {
        columns: [
            { field: 'nome_empresa', text: 'Empresa', sort: true },
            { field: 'tipo_descricao', text: 'Tipo', sort: true },
            { field: 'corte_hr', text: 'Corte', sort: true },
            { field: 'trimestre', text: 'Trimestre', sort: true },
        ],
        data: cortes,
    };

    function exportCSV() {
        const dados = cortes.map((it) => ({
            Empresa: it.nome_empresa,
            Tipo: it.tipo_descricao,
            Corte: it.corte_hr,
            Trimestre: it.trimestre,
        }));

        const file = XLSX.utils.book_new();
        const sheet = XLSX.utils.json_to_sheet(dados);
        XLSX.utils.book_append_sheet(file, sheet, 'Datas de cortes');
        XLSX.writeFile(file, `pdd_datas_cortes.xlsx`);
    }

    return (
        <PageLoader active={!!!globalState.empresas || isFetching}>
            <div id="pdd-page" className="page-content">
                <PageHeader />

                {!registros.length && (
                    <Segment className="subcontent">
                        <div className="row flex-grow-1">
                            {acao !== 'cortes' && (
                                <div className="col d-flex flex-column">
                                    <div className="row mx-0">
                                        <div className="col">
                                            <Header as="h3">
                                                <Icon.Group size="big">
                                                    <Icon name="tasks" className="primary-color" />
                                                </Icon.Group>
                                                <Header.Content className="primary-color">
                                                    Ações
                                                    <Header.Subheader className="secondary-color">
                                                        <strong>Escolha a ação desejada</strong>
                                                    </Header.Subheader>
                                                </Header.Content>
                                            </Header>
                                        </div>
                                    </div>
                                    <div className="row flex-grow-1 flex-column">
                                        <div className="col d-flex align-items-center flex-column justify-content-center">
                                            <Button
                                                icon
                                                className="w-50 btn-primary"
                                                color="blue"
                                                onClick={() => setAcao('resumo')}
                                                size="small"
                                                fluid
                                            >
                                                <Icon name="info" /> Resumo
                                            </Button>
                                            <Button
                                                icon
                                                className="w-50 btn-primary mt-2"
                                                color="blue"
                                                onClick={() => setAcao('gerar')}
                                                size="small"
                                                fluid
                                            >
                                                <Icon name="add" /> Gerar
                                            </Button>
                                            <Button
                                                icon
                                                className="w-50 btn-primary mt-2"
                                                color="blue"
                                                onClick={() => setAcao('relatorios')}
                                                size="small"
                                                fluid
                                            >
                                                <Icon name="clipboard list" /> Relatórios
                                            </Button>
                                            <Button
                                                icon
                                                className="w-50 btn-primary mt-2"
                                                color="blue"
                                                onClick={() => setAcao('cortes')}
                                                size="small"
                                                fluid
                                            >
                                                <Icon name="calendar alternate" /> Datas de cortes
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {acao === 'resumo' && (
                                <div className="col d-flex flex-column flex-grow-1">
                                    <Header as="h3" className="mb-0">
                                        <Icon.Group size="big">
                                            <Icon name="info" className="primary-color" />
                                        </Icon.Group>
                                        <Header.Content className="primary-color">
                                            Resumo
                                            <Header.Subheader className="secondary-color">
                                                <strong>Dados</strong>
                                            </Header.Subheader>
                                        </Header.Content>
                                    </Header>
                                    <div className="d-flex flex-column flex-grow-1 align-items-center justify-content-center">
                                        <Resumo {...resumoProps} />
                                    </div>
                                </div>
                            )}
                            {acao === 'gerar' && (
                                <div className="col d-flex flex-column flex-grow-1">
                                    <Header as="h3">
                                        <Icon.Group size="big">
                                            <Icon
                                                name="file text outline"
                                                className="primary-color"
                                            />
                                            <Icon corner name="add" className="primary-color" />
                                        </Icon.Group>
                                        <Header.Content className="primary-color">
                                            Gerar PDD
                                            <Header.Subheader className="secondary-color">
                                                <strong>Selecionar Faturas/Cheques</strong>
                                            </Header.Subheader>
                                        </Header.Content>
                                    </Header>
                                    <div className="d-flex flex-column flex-grow-1 align-items-center justify-content-center">
                                        <GerarForm {...gerarFormProps} />
                                    </div>
                                </div>
                            )}
                            {acao === 'relatorios' && (
                                <div className="col d-flex flex-column flex-grow-1">
                                    <Header as="h3">
                                        <Icon.Group size="big">
                                            <Icon name="clipboard list" className="primary-color" />
                                        </Icon.Group>
                                        <Header.Content className="primary-color">
                                            Relatórios
                                            <Header.Subheader className="secondary-color">
                                                <strong>Visualizar PDDs gerados</strong>
                                            </Header.Subheader>
                                        </Header.Content>
                                    </Header>
                                    <div className="d-flex flex-column flex-grow-1 align-items-center justify-content-center">
                                        <RelatoriosForm {...relatoriosFormProps} />
                                    </div>
                                </div>
                            )}
                            {acao === 'cortes' && (
                                <div className="col d-flex flex-column flex-grow-1">
                                    <Header as="h3" className="d-print-none">
                                        <Icon.Group size="big">
                                            <Icon
                                                name="calendar alternate"
                                                className="primary-color"
                                            />
                                        </Icon.Group>
                                        <Header.Content className="primary-color">
                                            Datas de corte
                                            <Header.Subheader className="secondary-color">
                                                <strong>Últimas operações</strong>
                                            </Header.Subheader>
                                        </Header.Content>
                                        <Button
                                            floated="right"
                                            className="btn-red"
                                            size="mini"
                                            onClick={() => setAcao('resumo')}
                                        >
                                            <Icon name="arrow left" /> Voltar
                                        </Button>
                                        <Button
                                            floated="right"
                                            size="mini"
                                            className="ml-auto btn-primary"
                                            onClick={() => window.print()}
                                        >
                                            <Icon name="print" /> Imprimir
                                        </Button>
                                        <Button
                                            color="teal"
                                            floated="right"
                                            className="mr-1"
                                            size="mini"
                                            onClick={exportCSV}
                                            content="Exportar"
                                            icon="file excel outline"
                                        />
                                    </Header>
                                    <div className="d-flex flex-column flex-grow-1 align-items-center justify-content-center">
                                        <Table
                                            id="table-cortes"
                                            size="small"
                                            compact
                                            fixed
                                            sortable
                                            selectable
                                            {...config}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </Segment>
                )}
                {registros.length > 0 && acao === 'gerar' && (
                    <Segment className="subcontent">
                        <GerarExibicao {...gerarExibicaoProps} />
                    </Segment>
                )}
                {registros.length > 0 && acao === 'relatorios' && (
                    <Segment className="subcontent">
                        <RelatoriosExibicao {...relatoriosExibicaoProps} />
                    </Segment>
                )}
            </div>
        </PageLoader>
    );
}

export default React.memo(PerdasDevedoresDuvidososPage);
